import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";
import { scrollToId } from "../../lib/smoothScroll";

const LINKS = [
  ["solutions", "solutions"],
  ["manifesto", "manifesto"],
  ["markets", "markets"],
  ["financing", "financing"],
  ["economics", "economics"],
  ["ecosystem", "ecosystem"],
  ["founders", "founders"],
];

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (fn) => () => { setOpen(false); fn(); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 border-b border-technical backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "bg-[#f8f7f4]/90 shadow-[0_1px_0_rgba(22,24,27,0.08)]" : "bg-[#f8f7f4]/70"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 sm:px-8 h-[68px]">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("top")}
          className="flex items-center gap-3 shrink-0"
        >
          <img src="/assets/margtor_logo.webp" alt="MargTor" className="h-9 w-9 object-contain mix-blend-multiply" />
          <span className="font-display font-black tracking-tight text-lg text-ink">
            MARG<span className="text-red-brand">TOR</span>
          </span>
          <span className="hidden md:max-lg:inline-block 2xl:inline-block font-mono-tech text-[10px] tracking-[0.2em] text-[#525964] border border-technical px-2 py-1">
            KE • IN • PL
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-5 min-w-0">
          {LINKS.map(([key, id]) => (
            <button
              key={key}
              data-testid={`nav-link-${key}`}
              onClick={() => scrollToId(id)}
              className={`font-mono-tech text-[10px] xl:text-[10.5px] uppercase tracking-[0.1em] xl:tracking-[0.14em] text-[#525964] hover:text-red-brand transition-colors duration-200 whitespace-nowrap ${["manifesto", "markets", "ecosystem"].includes(key) ? "hidden min-[1600px]:inline" : ""}`}
            >
              {t.nav[key]}
            </button>
          ))}
          <button
            data-testid="nav-link-knowledge"
            onClick={() => navigate("/knowledge")}
            className="font-mono-tech text-[10px] xl:text-[10.5px] uppercase tracking-[0.1em] xl:tracking-[0.14em] text-red-brand/80 hover:text-red-brand transition-colors duration-200 whitespace-nowrap"
          >
            {t.nav.knowledge}
          </button>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden min-[2200px]:flex items-center gap-2 whitespace-nowrap font-mono-tech text-[10px] tracking-[0.15em] text-[#525964]">
            <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-red-brand" />
            {t.nav.ops}
          </span>
          <div
            data-testid="nav-language-toggle"
            className="flex items-center border border-technical bg-white/60 p-0.5"
          >
            {["en", "sw", "pl", "fr", "zh"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-1.5 sm:px-2 py-1 font-mono-tech text-[10.5px] font-semibold uppercase tracking-wide transition-colors duration-200 ${
                  lang === l ? "bg-red-brand text-[#f8f7f4]" : "text-[#525964] hover:text-ink"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="nav-cta-partner"
            onClick={() => navigate("/partners")}
            className="chamfer-sm hidden sm:inline-block whitespace-nowrap bg-red-brand px-3 xl:px-4 py-2 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] transition-colors duration-200 hover:bg-[#5f1019]"
          >
            {t.nav.cta}
          </button>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden flex h-9 w-9 items-center justify-center border border-technical text-ink hover:text-red-brand transition-colors"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav"
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-technical bg-[#f8f7f4] px-5 sm:px-8 py-4"
          >
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1">
              {LINKS.map(([key, id]) => (
                <li key={key}>
                  <button data-testid={`nav-mobile-${key}`} onClick={go(() => { navigate("/"); setTimeout(() => scrollToId(id), 350); })} className="w-full py-2.5 text-left font-mono-tech text-[11px] uppercase tracking-[0.18em] text-[#525964] hover:text-red-brand">
                    {t.nav[key]}
                  </button>
                </li>
              ))}
              <li>
                <button data-testid="nav-mobile-knowledge" onClick={go(() => navigate("/knowledge"))} className="w-full py-2.5 text-left font-mono-tech text-[11px] uppercase tracking-[0.18em] text-red-brand">
                  {t.nav.knowledge}
                </button>
              </li>
              <li>
                <button data-testid="nav-mobile-partners" onClick={go(() => navigate("/partners"))} className="w-full py-2.5 text-left font-mono-tech text-[11px] uppercase tracking-[0.18em] text-ink">
                  {t.nav.cta}
                </button>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
